/* eslint-disable import/prefer-default-export */
import * as React from "react";
import {
  Button,
  FormField,
  Heading,
  Icon,
  Modal,
  TextInput,
} from "@components";
import classNames from "classnames";

import { useProductContext } from "../../context/ProductContext";
import { addBISSignupEvent } from "../../services/elevar/events";
import { isValidEmail } from "../util";

import constants from "./selectors";

const { useState } = React;
export const SWATCH_PRODUCT_ID = "7289999491177";

export interface BackInStockModalProps {
  /** Title of product */
  productTitle: string;
  /** Variant of product */
  productVariant: string;
  /** Variant ID of product */
  variantId: string;
  /** Function to invoke when form is submitted */
  onSubmit?: () => void;
  /** Function to invoke when modal is dismissed */
  onDismiss: () => void;
  /** Boolean to indicate an open modal */
  isOpen: boolean;
}

/**
 * A modal with an email capture form that pops up when an item is out of stock
 */
export function BackInStockModal({
  productTitle,
  productVariant,
  variantId,
  onSubmit,
  isOpen,
  onDismiss,
}: BackInStockModalProps) {
  const [inputValue, setInputValue] = useState("");
  const [invalid, setInvalid] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleOnSubmit = () => {
    const isValid = isValidEmail(inputValue);
    if (isValid) {
      addBISSignupEvent({
        customer_email: inputValue,
        product_variant: variantId,
      });
      if (onSubmit) onSubmit();
      setSubmitted(true);
      setInvalid(false);
    } else {
      setInvalid(true);
    }
  };
  const clearValues = () => {
    onDismiss();
    setSubmitted(false);
    setInputValue("");
  };
  const {
    variant: { backInStock },
  } = useProductContext();

  return (
    <Modal
      isOpen={isOpen}
      onDismiss={clearValues}
      containerClassName="w-screen h-screen full:w-[unset] full:h-[unset] p-0 full:p-4"
      childContainerClassName="relative px-6 full:px-10 py-10 max-w-full w-full h-full full:w-[640px] full:h-[unset]"
    >
      <div className="absolute text-right top-7 right-5 full:right-8">
        <Button
          onClick={clearValues}
          variant="unstyled"
          className="w-8 h-8 text-slate"
        >
          <Icon name="close" />
        </Button>
      </div>
      <div>
        <div className="pb-3 full:pb-6 pt-3 border-b border-b-[#f5f5f5] pr-4">
          <Heading
            as="h3"
            className="text-lg capitalize full:text-xl leading-eighth"
          >
            {backInStock.title}
          </Heading>
        </div>
        <form className="mt-6 full:mt-8">
          {backInStock.showMetadata ? (
            <div className="mb-6">
              <p>{productTitle}</p>
              <p className="pt-3">{productVariant}</p>
            </div>
          ) : null}
          <FormField error={invalid ? "Please enter a valid email" : undefined}>
            <TextInput
              id="bis-email-input"
              type="email"
              placeholder="Enter email address"
              data-testid={constants.input}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={() => setInvalid(false)}
              invalid={invalid}
              inputClasses="w-full rounded-md placeholder:text-slate px-3 full:px-4 py-3 full:py-4"
              inputContainerClasses={classNames("border-1 rounded-md", {
                "border-blush": invalid,
                "border-[#ececec]": !invalid,
              })}
            />
          </FormField>

          <Button
            onClick={handleOnSubmit}
            data-testid={constants.submit}
            className="w-full my-4 full:my-6 bg-pine"
          >
            <span className="text-sm uppercase">
              {submitted ? backInStock.ctaSuccess : backInStock.cta}
            </span>
          </Button>
        </form>
        <p className="text-center text-slate">
          {submitted ? backInStock.subtitleSuccess : backInStock.subtitle}
        </p>
      </div>
    </Modal>
  );
}
